import { useState, useEffect, useRef } from "react";
import { Head, usePage } from "@inertiajs/react";
import VimeoPlayer from "@vimeo/player";
import * as iframeApiLoader from "@kinescope/player-iframe-api-loader";
import { useTranslation } from "react-i18next";

import Layout, { Title } from "/Layout";
import { useHeaderHeight } from "/utils/header-height-context";
import ProjectTypes from "/components/Projects/ProjectTypes";
import ContactModal from "/components/Projects/ContactModal";
import ContactButton from "/components/Projects/ContactButton";
import LottieLayout from "/components/Projects/Layouts/Lottie";
import ImageLayout from "/components/Projects/Layouts/Image";
import VimeoLayout from "/components/Projects/Layouts/Vimeo";
import KinescopeLayout from "/components/Projects/Layouts/Kinescope";

const ProjectShow = ({
  project,
}) => {
  const { t } = useTranslation();
  const headerHeight = useHeaderHeight();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const playerRefs = useRef({});

  useEffect(() => {
    project.layouts.forEach((currentLayout) => {
      if (currentLayout.layout_type_codename !== "video") return;

      handlePlayer(currentLayout);
    });
  }, [project.layouts]);

  const handlePlayer = async (currentLayout) => {
    const currentPlayer = await findOrCreatePlayer(currentLayout);
    const isLoop = currentLayout.play_mode === "loop";

    if (typeof currentPlayer.ready === "function") {
      currentPlayer.ready().then(() => {
        if (isLoop) currentPlayer.play();
      });
    } else {
      console.log("KINESCOPE PLAYER HANDLING");
      if (isLoop) await currentPlayer.play();
    }
  };

  const findOrCreatePlayer = async (layout) => {
    if (playerRefs.current[layout.id]) return playerRefs.current[layout.id];

    if (/https?:\/\/vimeo\.com/.test(layout.video_url)) {
      playerRefs.current[layout.id] = new VimeoPlayer(`vimeo-${layout.id}`);
    } else if (/https?:\/\/kinescope\.io/.test(layout.video_url)) {
      const isLoop = layout.play_mode === "loop";
      const factory = await iframeApiLoader.load();
      playerRefs.current[layout.id] = await factory.create(
        `kinescope-${layout.id}`,
        {
          url: layout.video_url,
          behavior: {
            autoPause: false,
            loop: isLoop,
            muted: isLoop,
          },
          ui: {
            controls: !isLoop,
          }
        }
      );
      playerRefs.current[layout.id].on('pause', () => {
        console.log('pause triggered');
      });
    }

    return playerRefs.current[layout.id];
  };

  return (
    <>
      <Head>
        {project.og_title && (<meta head-key="og:title" key="og:title" property="og:title" content={project.og_title} />)}
        {project.og_description && (<meta head-key="og:description" key="og:description" property="og:description" content={project.og_description} />)}
        {project.og_image.type && (<meta key="og:image:type" property="og:image:type" content={project.og_image.type} />)}
        {project.og_image.url && (<meta key="og:image" property="og:image" content={project.og_image.url} />)}
        {project.og_image.url && (<meta key="og:image:width" property="og:image:width" content="1200" />)}
        {project.og_image.url && (<meta key="og:image:height" property="og:image:height" content="630" />)}

        {project.og_title && (<meta head-key="twitter:title" key="twitter:title" name="twitter:title" content={project.og_title} />)}
        {project.og_description && (<meta head-key="twitter:description" key="twitter:description" name="twitter:description" content={project.og_description} />)}
        {project.og_image.url && (<meta head-key="twitter:card" key="twitter:card" name="twitter:card" content="summary_large_image" />)}
        {project.og_image.type && (<meta key="twitter:image:type" name="twitter:image:type" content={project.og_image.type} />)}
        {project.og_image.url && (<meta key="twitter:image" name="twitter:image" content={project.og_image.url} />)}
        {project.og_image.url && (<meta key="twitter:image:width" name="twitter:image:width" content="1200" />)}
        {project.og_image.url && (<meta key="twitter:image:height" name="twitter:image:height" content="630" />)}
      </Head>

      <Title>
        <h1>{t("projects.titlePrefix")}&nbsp;
          <div className="project-desktop-title">
            <span>{project.title}</span>
            <ProjectTypes project_types={project.project_types} />
          </div>
        </h1>
      </Title>

      <ContactButton setIsModalOpen={setIsContactModalOpen} mobile={true} />

      <div className="project-container">
        <div className="project-title--mobile">
          <h1>{project.title}</h1>

          <ProjectTypes project_types={project.project_types} />
        </div>

        <div className="flex-row full-width gap-3">
          <div className="project-container__sidepanel__wrapper">
            <div className="project-container__sidepanel" style={{ top: headerHeight + 'px' }}>
              <ContactButton setIsModalOpen={setIsContactModalOpen} />
            </div>
          </div>

          <div className="project-layouts">
            {project.layouts.map(layout => (
              <div key={layout.id} className="project-layout">
                <div className="project-layout__viewport">
                  {layout.layout_type_codename === "image" && (
                    <ImageLayout key={layout.id} layout={layout} />
                  )}

                  {layout.layout_type_codename === "video" && /https?:\/\/vimeo\.com/.test(layout.video_url) && (
                    <VimeoLayout key={layout.id} layout={layout} />
                  )}

                  {layout.layout_type_codename === "video" && /https?:\/\/kinescope\.io/.test(layout.video_url) && (
                    <KinescopeLayout key={layout.id} layout={layout} />
                  )}

                  {layout.layout_type_codename === "lottie" && (
                    <LottieLayout key={layout.id} layout={layout} />
                  )}
                </div>
                <div className="project-layout__info">
                  {layout.description}
                </div>
              </div>
            ))}

            <Teams teams={project.teams} />
          </div>
        </div>
      </div>

      <ContactModal isOpen={isContactModalOpen} setIsOpen={setIsContactModalOpen} />
    </>
  );
};

ProjectShow.layout = (page) => <Layout children={page} />;

function Teams({
  teams
}) {
  return (
    <div className="project-teams">
      {teams.map(team => (
        <div key={team.id} className="project-team">
          <div className="project-team__name">{team.name}</div>

          {team.members.map((member, index) => (
            <div key={index} className="project-team-member">
              <div className="project-team-member__name">{member.name}</div>
              <div className="project-team-member__position">{member.position}</div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default ProjectShow;
